import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { fmtINR, fmtDate } from "@/lib/format";
import { ShieldCheck, Download, FileSpreadsheet, Clock, Info, Lock } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

/**
 * Auditor / CA public view (UNAUTHENTICATED — token-scoped).
 *
 * Fetches meta + data from the tokenised endpoint. Shows only the
 * sections the owner enabled. Card + bank numbers always masked.
 */
export default function AuditorView() {
    const { token } = useParams();
    const [meta, setMeta] = useState(null);
    const [bundle, setBundle] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const m = await axios.get(`${API}/auditor/view/${token}/meta`);
                if (cancelled) return;
                setMeta(m.data);
                if (m.data.revoked) { setErr("This link has been revoked."); return; }
                const b = await axios.get(`${API}/auditor/view/${token}`);
                if (!cancelled) setBundle(b.data);
            } catch (e) {
                if (!cancelled) setErr(e?.response?.data?.detail || "Link invalid or expired.");
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    async function downloadPdf() {
        const { jsPDF } = await import("jspdf");
        await import("jspdf-autotable");
        const doc = new jsPDF();
        doc.setFontSize(16); doc.text(meta.label, 14, 18);
        doc.setFontSize(10); doc.setTextColor(100);
        doc.text(`Period: ${meta.period_from} to ${meta.period_to}`, 14, 26);
        doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 32);
        doc.text(`Owner: ${meta.owner_name}`, 14, 38);
        let y = 46;
        const S = bundle.sections;
        if (S.financial_position) {
            doc.setFontSize(12); doc.setTextColor(0); doc.text("Financial Position", 14, y); y += 6;
            doc.autoTable({ startY: y, head: [["Line", "Amount (₹)"]], body: [
                ["Total Assets", S.financial_position.total_assets.toLocaleString("en-IN")],
                ["Total Liabilities", S.financial_position.total_liabilities.toLocaleString("en-IN")],
                ["Net Worth", S.financial_position.net_worth.toLocaleString("en-IN")],
                ["Bank Balances", S.financial_position.bank_balances.toLocaleString("en-IN")],
                ["Investments (current)", S.financial_position.investments_current.toLocaleString("en-IN")],
                ["Assets Registry", S.financial_position.assets_registry.toLocaleString("en-IN")],
                ["Credit Card Outstanding", S.financial_position.credit_card_outstanding.toLocaleString("en-IN")],
            ] });
            y = doc.lastAutoTable.finalY + 8;
        }
        if (S.expenses) {
            doc.text("Expense Summary", 14, y); y += 6;
            doc.autoTable({ startY: y, head: [["Category", "Amount (₹)"]], body: Object.entries(S.expenses.by_category).map(([k, v]) => [k, v.toLocaleString("en-IN")]) });
            y = doc.lastAutoTable.finalY + 8;
        }
        if (S.tax) {
            doc.text("Tax Deductions", 14, y); y += 6;
            doc.autoTable({ startY: y, head: [["Section", "Amount (₹)"]], body: Object.entries(S.tax.by_section).map(([k, v]) => [k, v.toLocaleString("en-IN")]) });
        }
        doc.save(`${meta.label.replace(/[^a-z0-9]+/gi, "_")}.pdf`);
    }

    async function downloadXlsx() {
        const XLSX = await import("xlsx");
        const wb = XLSX.utils.book_new();
        const S = bundle.sections;
        if (S.financial_position) {
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([
                { Line: "Total Assets", Amount: S.financial_position.total_assets },
                { Line: "Total Liabilities", Amount: S.financial_position.total_liabilities },
                { Line: "Net Worth", Amount: S.financial_position.net_worth },
                { Line: "Bank Balances", Amount: S.financial_position.bank_balances },
                { Line: "Investments Current", Amount: S.financial_position.investments_current },
                { Line: "Assets Registry", Amount: S.financial_position.assets_registry },
                { Line: "Credit Card Outstanding", Amount: S.financial_position.credit_card_outstanding },
            ]), "Position");
            if (S.financial_position.cards?.length) {
                XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(S.financial_position.cards), "Cards");
            }
            if (S.financial_position.bank_accounts?.length) {
                XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(S.financial_position.bank_accounts), "Bank Accounts");
            }
        }
        if (S.expenses) {
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
                Object.entries(S.expenses.by_category).map(([Category, Amount]) => ({ Category, Amount }))
            ), "Expenses");
        }
        if (S.income) {
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
                Object.entries(S.income.by_source).map(([Source, Amount]) => ({ Source, Amount }))
            ), "Income");
        }
        if (S.tax) {
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
                Object.entries(S.tax.by_section).map(([Section, Amount]) => ({ Section, Amount }))
            ), "Tax");
        }
        if (S.capital_purchases?.items?.length) {
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(S.capital_purchases.items), "Capital Purchases");
        }
        if (S.documents?.length) {
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(S.documents), "Documents");
        }
        XLSX.writeFile(wb, `${meta.label.replace(/[^a-z0-9]+/gi, "_")}.xlsx`);
    }

    if (err) return (
        <div className="min-h-screen flex items-center justify-center bg-stone-50 dark:bg-stone-950">
            <div className="max-w-md w-full bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-2xl p-8 text-center">
                <Lock size={26} className="mx-auto text-rose-500 mb-3" />
                <div className="text-sm text-stone-600 dark:text-stone-300">{err}</div>
            </div>
        </div>
    );
    if (!meta || !bundle) return <div className="min-h-screen flex items-center justify-center text-sm text-stone-500">Loading…</div>;
    const S = bundle.sections;

    return (
        <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
            <header className="bg-white dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800 sticky top-0 z-10">
                <div className="max-w-[1200px] mx-auto px-6 py-3 flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                        <BrandLogo variant="dark" height={22} />
                        <span className="hidden sm:inline text-stone-300">/</span>
                        <span className="text-sm font-semibold text-stone-800 dark:text-white">Auditor View</span>
                    </div>
                    {meta.allow_downloads && (
                        <div className="flex gap-2">
                            <button onClick={downloadPdf} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-stone-200 dark:border-stone-700 text-[12px] text-stone-700 dark:text-stone-200 hover:border-stone-300" data-testid="auditor-download-pdf">
                                <Download size={12} /> PDF
                            </button>
                            <button onClick={downloadXlsx} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-stone-200 dark:border-stone-700 text-[12px] text-stone-700 dark:text-stone-200 hover:border-stone-300" data-testid="auditor-download-xlsx">
                                <FileSpreadsheet size={12} /> Excel
                            </button>
                        </div>
                    )}
                </div>
            </header>

            <main className="max-w-[1200px] mx-auto px-6 py-6" data-testid="auditor-view-body">
                <div className="mb-4">
                    <h1 className="text-2xl font-bold text-stone-900 dark:text-white">{meta.label}</h1>
                    <div className="mt-1 flex items-center gap-3 flex-wrap text-[13px] text-stone-500 dark:text-stone-400">
                        <span className="inline-flex items-center gap-1"><Clock size={12} /> {meta.period_from} to {meta.period_to}</span>
                        <span>·</span>
                        <span>Owner: {meta.owner_name}</span>
                        <span>·</span>
                        <span>Expires {fmtDate(meta.expires_at)}</span>
                    </div>
                </div>

                <div className="mb-4 bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-900 rounded-lg px-4 py-2 text-[13px] text-emerald-800 dark:text-emerald-200 flex items-start gap-2">
                    <ShieldCheck size={14} className="mt-0.5 shrink-0" />
                    <span>This is a read-only, period-scoped snapshot. Card and bank account numbers are masked to last 4 digits. Every access is logged.</span>
                </div>

                {S.financial_position && <FinancialPositionSection s={S.financial_position} />}
                {S.income && <IncomeSection s={S.income} />}
                {S.expenses && <ExpensesSection s={S.expenses} />}
                {S.tax && <TaxSection s={S.tax} />}
                {S.capital_purchases && <CapitalPurchasesSection s={S.capital_purchases} />}
                {S.reconciliation && <ReconciliationSection s={S.reconciliation} />}
                {S.documents && <DocumentsSection s={S.documents} />}
                {S.missing_records && <MissingRecordsSection s={S.missing_records} />}
            </main>
        </div>
    );
}

function SectionShell({ title, subtitle, children, testId }) {
    return (
        <section className="mb-6 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-2xl p-5" data-testid={testId}>
            <div className="mb-3">
                <h2 className="text-lg font-bold text-stone-900 dark:text-white">{title}</h2>
                {subtitle && <p className="text-[12px] text-stone-500 dark:text-stone-400 mt-0.5">{subtitle}</p>}
            </div>
            {children}
        </section>
    );
}

function KeyValue({ items }) {
    return (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {items.map((it) => (
                <div key={it.label}>
                    <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">{it.label}</div>
                    <div className={`text-lg font-bold tabular-nums mt-1 ${it.emphasis ? "text-stone-900 dark:text-white" : "text-stone-800 dark:text-stone-100"}`}>{it.value}</div>
                </div>
            ))}
        </div>
    );
}

function Table({ headers, rows, testId }) {
    return (
        <div className="overflow-x-auto" data-testid={testId}>
            <table className="w-full text-[13px]">
                <thead>
                    <tr className="text-left text-[11px] uppercase tracking-widest text-stone-500 border-b border-stone-200 dark:border-stone-700">
                        {headers.map((h, i) => <th key={i} className={`py-2 ${i === headers.length - 1 ? "text-right" : ""}`}>{h}</th>)}
                    </tr>
                </thead>
                <tbody className="divide-y divide-stone-100 dark:divide-stone-800">
                    {rows.map((r, i) => (
                        <tr key={i}>
                            {r.map((c, j) => <td key={j} className={`py-2 ${j === r.length - 1 ? "text-right tabular-nums font-medium text-stone-900 dark:text-white" : "text-stone-700 dark:text-stone-200"}`}>{c}</td>)}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function FinancialPositionSection({ s }) {
    return (
        <SectionShell title="Financial Position" subtitle="Assets, liabilities and net worth snapshot." testId="section-financial-position">
            <KeyValue items={[
                { label: "Total Assets", value: fmtINR(s.total_assets), emphasis: true },
                { label: "Total Liabilities", value: fmtINR(s.total_liabilities), emphasis: true },
                { label: "Net Worth", value: fmtINR(s.net_worth), emphasis: true },
                { label: "Bank Balances", value: fmtINR(s.bank_balances) },
                { label: "Investments", value: fmtINR(s.investments_current) },
                { label: "Assets Registry", value: fmtINR(s.assets_registry) },
                { label: "Credit-card Outstanding", value: fmtINR(s.credit_card_outstanding) },
            ]} />
            {s.cards?.length > 0 && (
                <div className="mt-5">
                    <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold mb-2">Credit Cards ({s.cards.length})</div>
                    <Table
                        headers={["Bank", "Card", "•••• last 4", "Outstanding", "Limit"]}
                        rows={s.cards.map((c) => [c.bank_name || "—", c.card_name || "—", `•••• ${c.last_4 || "—"}`, fmtINR(c.outstanding), fmtINR(c.credit_limit)])}
                        testId="auditor-cards-table"
                    />
                </div>
            )}
            {s.bank_accounts?.length > 0 && (
                <div className="mt-5">
                    <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold mb-2">Bank Accounts ({s.bank_accounts.length})</div>
                    <Table
                        headers={["Bank", "Holder", "Type", "Account", "Balance"]}
                        rows={s.bank_accounts.map((b) => [b.bank_name || "—", b.account_holder || "—", b.account_type || "—", b.account_number_masked || "—", fmtINR(b.current_balance)])}
                        testId="auditor-bank-accounts-table"
                    />
                </div>
            )}
        </SectionShell>
    );
}

function IncomeSection({ s }) {
    return (
        <SectionShell title="Income Summary" subtitle={`${s.count} record(s) · Total ${fmtINR(s.total)}`} testId="section-income">
            {Object.keys(s.by_source).length === 0 ? (
                <p className="text-sm text-stone-500">No income recorded in this period.</p>
            ) : (
                <Table headers={["Source", "Amount"]} rows={Object.entries(s.by_source).map(([k, v]) => [k, fmtINR(v)])} testId="auditor-income-table" />
            )}
        </SectionShell>
    );
}
function ExpensesSection({ s }) {
    return (
        <SectionShell title="Expense Summary" subtitle={`${s.count} record(s) · Debits ${fmtINR(s.total_debit)}`} testId="section-expenses">
            <Table headers={["Category", "Amount"]} rows={Object.entries(s.by_category).sort((a, b) => b[1] - a[1]).map(([k, v]) => [k, fmtINR(v)])} testId="auditor-expenses-table" />
        </SectionShell>
    );
}
function TaxSection({ s }) {
    return (
        <SectionShell title="Tax Records" subtitle={`${s.count} deduction proof(s) · Total ${fmtINR(s.total)}`} testId="section-tax">
            {Object.keys(s.by_section).length === 0 ? (
                <p className="text-sm text-stone-500">No tax proofs in this period.</p>
            ) : (
                <Table headers={["Section", "Amount"]} rows={Object.entries(s.by_section).map(([k, v]) => [k, fmtINR(v)])} testId="auditor-tax-table" />
            )}
        </SectionShell>
    );
}
function CapitalPurchasesSection({ s }) {
    return (
        <SectionShell title="Capital Purchases" subtitle={`${s.count} asset(s) acquired · Total ${fmtINR(s.total)}`} testId="section-capital">
            {s.items.length === 0 ? (
                <p className="text-sm text-stone-500">No capital purchases in this period.</p>
            ) : (
                <Table headers={["Name", "Category", "Date", "Vendor", "Price"]} rows={s.items.map((i) => [i.name, i.category || "—", fmtDate(i.purchase_date), i.vendor || "—", fmtINR(i.purchase_price)])} testId="auditor-capital-table" />
            )}
        </SectionShell>
    );
}
function ReconciliationSection({ s }) {
    return (
        <SectionShell title="Reconciliation Status" testId="section-reconciliation">
            <KeyValue items={[
                { label: "Statements in period", value: s.statements_in_period },
                { label: "Statements reconciled", value: s.statements_reconciled },
                { label: "Payments pending posting", value: s.payments_pending_posting },
            ]} />
        </SectionShell>
    );
}
function DocumentsSection({ s }) {
    return (
        <SectionShell title="Documents" subtitle={`${s.length} document(s) on file (metadata only — files not shared)`} testId="section-documents">
            {s.length === 0 ? (
                <p className="text-sm text-stone-500">No documents on file.</p>
            ) : (
                <Table headers={["Title", "Category", "Uploaded"]} rows={s.slice(0, 100).map((d) => [d.title || "—", d.category || "—", fmtDate(d.uploaded_at)])} testId="auditor-docs-table" />
            )}
        </SectionShell>
    );
}
function MissingRecordsSection({ s }) {
    return (
        <SectionShell title="Missing Records — data-quality flags" testId="section-missing">
            <KeyValue items={[
                { label: "Expenses > ₹1,000 missing receipts", value: s.expenses_missing_receipts_over_1k },
                { label: "Uncategorised expenses", value: s.expenses_uncategorised },
            ]} />
            <div className="mt-3 flex items-start gap-2 text-[12px] text-stone-500 dark:text-stone-400">
                <Info size={12} className="mt-0.5 shrink-0" />
                <span>These are records the owner may want to clean up before the audit.</span>
            </div>
        </SectionShell>
    );
}
